import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Inscription, Preloader, RawButton, Text, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { DropButton } from '@/components/controls/DropButton';

import { AuthorizedUser } from '../../authorizedUsers.types';

import useAuthorizedUsersList, { COLUMNS } from './hooks/useAuthorizedUsersList';

interface StyledButtonProps {
  $isWarning?: boolean;
}

const StyledDropdownButton = styled(Button)<StyledButtonProps>`
  height: 21px !important;
  min-height: 21px !important;
  ${({ theme }) => theme.fonts.text14Regular};
  font-weight: 400 !important;
  margin-bottom: ${({ theme }) => theme.spacings.s};
  color: ${({ theme, $isWarning }) => ($isWarning ? `${theme.colors.danger} !important` : '')};
  &:last-child {
    margin-bottom: 0;
  }
  &:hover {
    color: ${({ theme, $isWarning }) => ($isWarning ? theme.colors.danger : theme.colors.textAccent)};
  }
`;

interface Props {
  authorizedUsers: AuthorizedUser[];
  isLoading: boolean;
  onEdit: (user: AuthorizedUser) => void;
  onDeactivate: (id: string) => void;
}

const AuthorizedUsersList = ({ authorizedUsers, isLoading, onEdit, onDeactivate }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { formattedUsers } = useAuthorizedUsersList({ authorizedUsers, onEdit, onDeactivate });

  if (isLoading) {
    return (
      <Box pad={{ top: 'spacing24' }}>
        <Preloader testId="authorized-users-list_preloader" />
      </Box>
    );
  }

  if (!authorizedUsers.length) return null;

  if (isMobile) {
    return (
      <Box
        margin={{ top: 'spacing16' }}
        round="container1Round"
        background="module"
        pad="spacing4"
        direction="column"
        gap="spacing8"
        data-testid="authorized-users-list"
      >
        {formattedUsers.map((user) => (
          <Box
            key={user.id}
            direction="row"
            align="center"
            justify="between"
            background="canvas"
            round="container2Round"
            border={{ color: 'border2', size: '1px' }}
            pad="spacing16"
            data-testid={`authorized-users-list_item_${user.id}`}
          >
            <Box gap="spacing4">
              <Box
                background="module"
                round="button2Round"
                pad={{ horizontal: 'spacing6' }}
                width="fit-content"
              >
                <Text size="small" weight={500} data-testid={`authorized-users-list_item_${user.id}_relationship`}>{user.relationship}</Text>
              </Box>
              <Box gap="spacing2">
                <Text size="medium" weight={700} data-testid={`authorized-users-list_item_${user.id}_full-name`}>{user.firstName} {user.lastName}</Text>
                <Text size="small" color="textSecondary" data-testid={`authorized-users-list_item_${user.id}_date-of-birth`}>{user.dateOfBirth}</Text>
              </Box>
            </Box>
            <DropButton
              testId={`authorized-users-list_item_${user.id}`}
              contentStyle={{
                top: '30px',
                left: '-150px',
                padding: '12px',
                zIndex: 100,
                maxHeight: 'fit-content',
              }}
            >
              <Box gap="spacing4">
                <StyledDropdownButton
                  type="button"
                  data-testid={`authorized-users-list_item_${user.id}_edit`}
                  onClick={user.onEdit}
                >
                  {t('Edit')}
                </StyledDropdownButton>
                <StyledDropdownButton
                  type="button"
                  $isWarning
                  data-testid={`authorized-users-list_item_${user.id}_deactivate`}
                  onClick={user.onDeactivate}
                >
                  {t('Deactivate')}
                </StyledDropdownButton>
              </Box>
            </DropButton>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      margin={{ top: 'spacing24' }}
      round="container1Round"
      background="module"
      pad="spacing8"
      data-testid="authorized-users-list"
    >
      <Box
        direction="row"
        align="center"
        background="listHeaderBackgroundColor"
        round="container2Round"
        pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
      >
        {COLUMNS.map((col) => (
          <Box key={col.key} style={{ flex: col.flex }}>
            <Text size="small" weight="bold" color="listHeaderItemColor">
              {t(col.title)}
            </Text>
          </Box>
        ))}
      </Box>

      {formattedUsers.map((user) => (
        <Box
          key={user.id}
          direction="row"
          align="center"
          background="canvas"
          round="container2Round"
          border={{ color: 'border2', size: '1px' }}
          pad="spacing16"
          margin={{ top: 'spacing8' }}
          data-testid={`authorized-users-list_item_${user.id}`}
        >
          <Box style={{ flex: 1 }}>
            <Text size="medium" data-testid={`authorized-users-list_item_${user.id}_first-name`}>{user.firstName}</Text>
          </Box>
          <Box style={{ flex: 1 }}>
            <Text size="medium" data-testid={`authorized-users-list_item_${user.id}_last-name`}>{user.lastName}</Text>
          </Box>
          <Box style={{ flex: 1 }}>
            <Text size="medium" data-testid={`authorized-users-list_item_${user.id}_relationship`}>{user.relationship}</Text>
          </Box>
          <Box style={{ flex: 1 }}>
            <Text size="medium" data-testid={`authorized-users-list_item_${user.id}_date-of-birth`}>{user.dateOfBirth}</Text>
          </Box>
          <Box style={{ flex: '0 0 130px' }} direction="row" gap="spacing12">
            <RawButton
              data-testid={`authorized-users-list_item_${user.id}_edit`}
              onClick={user.onEdit}
            >
              <Inscription weight={400} color="textAccent">{t('Edit')}</Inscription>
            </RawButton>
            <RawButton
              data-testid={`authorized-users-list_item_${user.id}_deactivate`}
              onClick={user.onDeactivate}
            >
              <Inscription weight={400} color="danger">{t('Deactivate')}</Inscription>
            </RawButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default AuthorizedUsersList;
